import { useLocalStorageState } from "@outschool/local-storage";
import { Dispatch, SetStateAction, useState } from "react";

type ExpiringValueStorage<T> = {
  value: T;
  lastUsage: number;
};

/**
 * Persists a value until it has been un-used for a given duration.  Intended
 * to keep a value constant during a single user session.
 *
 * Stores value and last usage to local storage.
 */
export default function useExpiringValue<T>(
  key: string,
  defaultValue: T,
  duration: number
): [T, Dispatch<SetStateAction<T>>] {
  // use a single timestamp for rendering this component multiple times
  const [componentTime] = useState(Date.now());
  const [cache, setCache] = useLocalStorageState<ExpiringValueStorage<T>>(key, {
    value: defaultValue,
    lastUsage: componentTime,
  });
  const expired = componentTime - cache.lastUsage > duration;

  const value = expired ? defaultValue : cache.value;
  const setValue = (newValue: T) =>
    setCache({ value: newValue, lastUsage: componentTime });
  if (value !== cache.value || componentTime > cache.lastUsage) {
    setValue(value);
  }

  return [value, setValue];
}
